import React, { useMemo, ReactElement, Dispatch, SetStateAction } from 'react'
import { Link, Button, Box } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { makeStyles } from 'tss-react/mui'
import { TFunction } from 'i18next'
import DOMPurify from 'dompurify'

import { EditorSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import PaperContainer from '../../PaperContainer'
import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { TestRunRecord, MessageData, TestRunStates, TestRunsModel } from '../../../models/hardware-tests.model'
import { getHardwareTestMessage, getFormattedTestResult, getHardwareTestStatus } from '../../../utils'
import { ListActionMenu } from './hardware-test'
import { format, toZonedTime, BERLIN_TIME_ZONE } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  initialTestButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: theme.spacing(2),
  },
  messageTextContainer: {
    width: '100%',
    maxWidth: '550px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    a: {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },
}))

const renderUserLink = (value: string, _: number, element?: TestRunRecord): ReactElement => (
  <Link component={RouterLink} sx={{ textDecoration: 'none' }} to={`/overview/users/${element?.userId}`}>
    {value}
  </Link>
)

const renderMessageTextColumn = (
  value: MessageData,
  classes: Record<'initialTestButtonContainer' | 'messageTextContainer', string>,
  t: TFunction
): ReactElement => (
  <Box
    className={classes.messageTextContainer}
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(getFormattedTestResult(getHardwareTestMessage(value, t))),
    }}
  />
)

interface Props {
  data: TestRunsModel | null
  dataLoading: boolean
  pageSize: number
  page: number
  setPageSize: Dispatch<SetStateAction<number>>
  setPage: Dispatch<SetStateAction<number>>
  triggerHardwareTest: () => void
  isTriggered: boolean
  onRunTask: (testRun: TestRunRecord) => void
}

/**
 * Table component to display all test runs within the system
 */
const HardwareTestRuns: React.FC<Props> = ({
  data,
  dataLoading,
  page,
  pageSize,
  setPage,
  setPageSize,
  triggerHardwareTest,
  isTriggered,
  onRunTask,
}) => {
  const { classes } = useStyles()
  const { t, selectedAppLanguage } = useCustomTranslation()

  const tableHeaders: Header<TestRunRecord>[] = useMemo(() => {
    return [
      {
        label: t('system-details.hardware-tests.table.date'),
        value: 'startedAt',
        isSortable: false,
        width: '150px',
        valueRender: (value: string) => {
          const localTime = toZonedTime(new Date(value), BERLIN_TIME_ZONE)
          return format(localTime, t('common.format.date-fns-date-time-without-seconds'))
        },
      },
      {
        label: t('system-details.hardware-tests.table.user'),
        value: 'userName',
        isSortable: false,
        valueRender: renderUserLink,
      },
      {
        label: t('system-details.hardware-tests.table.test-id'),
        value: 'testRunId',
        isSortable: false,
      },
      {
        label: t('system-details.hardware-tests.table.test-name'),
        value: 'testId',
        isSortable: false,
      },
      {
        label: t('system-details.hardware-tests.table.state'),
        value: 'state',
        isSortable: false,
        valueRender: (state: TestRunStates) => getHardwareTestStatus(state, t),
      },
      {
        label: t('system-details.hardware-tests.table.result'),
        value: 'message',
        width: '550px',
        isSortable: false,
        valueRender: (value: MessageData) => renderMessageTextColumn(value, classes, t),
      },
      {
        label: '',
        value: 'testRunId',
        isSortable: false,
        valueRender: (value, index, item) => {
          if (!item) return

          return <ListActionMenu testRun={item} onRunTask={onRunTask} disableTestRun={isTriggered} />
        },
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppLanguage, isTriggered])

  return (
    <PaperContainer title={t('system-details.hardware-tests.title')}>
      {dataLoading ? (
        <EditorSkeleton />
      ) : (
        <TableComplete<TestRunRecord>
          data={data?.data || []}
          header={tableHeaders}
          resultLimit={pageSize}
          setResultLimit={setPageSize}
          resultPage={page}
          setResultPage={setPage}
          headerElevation={0}
          bodyElevation={0}
          footerElevation={0}
          dataTotal={data?.totalCount}
          coloredColumn
          manualPagination
        />
      )}
      <Box className={classes.initialTestButtonContainer}>
        <Button variant="contained" onClick={() => triggerHardwareTest()} disabled={isTriggered}>
          {t('system-details.hardware-tests.initial-test.button-text')}
        </Button>
      </Box>
    </PaperContainer>
  )
}

export default HardwareTestRuns
